import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { TrainDataService } from '../../services/train-data.service';
import { BehaviorSubject, merge, Observable, of, Subscription } from 'rxjs';
import { Train } from '../../models/train';
import { map, switchMap } from 'rxjs/operators';
import { TrainEditComponent } from '../train-edit/train-edit.component';
import { ConfirmationDialogService } from '../../../../shared/services/confirmation-dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { FilterUtils } from "primeng/utils";
import { OnDestroy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TableStateService } from '../../../../shared/services/table-state.service';
import { ExportFilenameService } from '../../../../shared/services/export-filename.service';
import { DataImportExportService } from '../../../../shared/services/data-import-export.service';

@Component({
  selector: 'app-train-list',
  template: require('./train-list.component.html'),
  styles: [require('./train-list.component.sass')]
})
export class TrainListComponent implements OnInit, OnDestroy {

  @ViewChild(TrainEditComponent) trainEdit: TrainEditComponent;

  tableConfiguration: any = {
    paginate: true,
    sortableMode: 'multiple',
    defaultPagination: 250,
    itemsPerPageOptions: [10,50,100,250,500],
    exportFilename: ExportFilenameService.generate('Train-Management')
  }

  columns: any[];

  private readonly refreshTrainList$ = new BehaviorSubject(undefined);
  trains$: Observable<(Train & {searchableReleasedDate?: string | null}) [] | undefined>;
  projectId: number;

  languageChangeSubscription: Subscription;

  constructor(
    private trainDataService: TrainDataService,
    @Inject('ProjectService') private projectService: any,
    private confirmationDialogService: ConfirmationDialogService,
    private translate: TranslateService,
    private messageService: MessageService,
    private datePipe: DatePipe,
    public tableState: TableStateService,
    public dataImportExportService: DataImportExportService
  ) {
    this.trains$ = this.refreshTrainList$.pipe(
        switchMap(() => merge(
            of(null),
            this.trainDataService.getTrainslist(this.projectId))
        ), map(trains => trains?.map(train => {
          return {
            ...train, 
            displayTypeName: train.displayTypeName || train.trainType,
            searchableReleasedDate: train.releasedDate && this.datePipe.transform(train.releasedDate, 'dd/MM/yyyy')
          }
        })))
    
    this.languageChangeSubscription = this.translate.onLangChange.subscribe(() => {
          this.refreshTrainTable();
    })
  }

  ngOnInit(): void {
    this.projectId = this.projectService.getSelectedProjectId();
    FilterUtils['releaseDateFilter'] = (value, filter): boolean => {
      return filter ? (value ? new Date(new Date(value).setHours(0,0,0,0)).getTime() == new Date(new Date(filter).setHours(0,0,0,0)).getTime() : false) : true;
    }
    FilterUtils['clearReleaseDateFilter'] = (value, filter): boolean => {
      return true;
    }
    FilterUtils['releaseDateContainsFilter'] = (value, filter): boolean => {
      return filter ? (value && this.datePipe.transform(value, 'dd/MM/yyyy')?.includes(filter) || false) : true;
    }

    this.refreshTrainTable();
  }

  refreshTrainTable = () => {
   this.columns = [{
      header: this.translate.instant('I18N_TRAIN.OFFICIAL_ID'),
      field: 'officialId',
      type: 'text'
    }, {
      header: this.translate.instant('I18N_TRAIN.RTL_ID'),
      field: 'relativeId',
      type: 'text'
    }, {
      header: this.translate.instant('I18N_GENERAL.NAME'),
      field: 'name',
      type: 'text'
    }, {
      header: this.translate.instant('I18N_TRAIN.CONNECTIVITY_SERVER'),
      field: 'connectivityServer',
      type: 'text'
    }, {
      header: this.translate.instant('I18N_TRAIN.TELEMETRY_SERVER'),
      field: 'telemetryServer',
      type: 'text'
    }, {
      header: this.translate.instant('I18N_TRAIN.MAINTAINER'),
      field: 'maintainer',
      type: 'text'
    }, {
      header: this.translate.instant('I18N_TRAIN.TRAIN_TYPE'),
      field: 'displayTypeName',
      type: 'text'
    }, {
      header: this.translate.instant('I18N_TRAIN.RELEASED_DATE'),
      field: 'releasedDate',
      type: 'text'
    }, {
      header: this.translate.instant('I18N_GENERAL.ACTIONS'),
      disableSorting: true,
      disableSearch: true,
      excludeExport: true
    }]
  
  }

  async addTrain() {
    await this.trainEdit.showModal();
  }

  async editTrain(train: Train) {
    await this.trainEdit.showModal(train);
  }

  reload = () => {
    this.refreshTrainList$.next(undefined);
  }

  onSaveTrain = async (train: Train) => {
    try {
      if (train && train.id) {
        await this.trainDataService.updateTrain(train, true);
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant("I18N_TRAIN.EDIT_TRAIN"),
          detail: this.translate.instant("I18N_TRAIN.TRAIN_EDIT_SUCCESSFUL")
        });
      } else {
        await this.trainDataService.updateTrain(train, false);
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant("I18N_TRAIN.CREATE_TRAIN"),
          detail: this.translate.instant("I18N_TRAIN.TRAIN_CREATION_SUCCESSFUL")
        });
      }
      this.reload();
    } catch (error) {
      if (train && train.id) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant("I18N_TRAIN.EDIT_TRAIN"),
          detail: error.message
        });
      } else {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant("I18N_TRAIN.CREATE_TRAIN"),
          detail: error.message
        });
      }
    }

  }


  deleteTrain = async (train: Train) => {
    if (train && train.id) {
      const confirmed = await   this.confirmationDialogService.confirm(
          `${this.translate.instant('I18N_TRAIN.DELETE_TRAIN')} ${train.name}`,
          this.translate.instant('I18N_TRAIN.ARE_YOU_SURE_TO_DELETE_THIS_TRAIN?'),
          'danger',
          this.translate.instant('I18N_TRAIN.DELETE_TRAIN'),
          this.translate.instant('I18N_GENERAL.CANCEL'),
          "lg");
      if (confirmed) {
        try {
          await this.trainDataService.deleteTrain(train.relativeId, this.projectId);
          this.reload();
        } catch (error) {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant("I18N_TRAIN.DELETE_TRAIN"),
            detail: error.message
          });
        }
      }
    }
  }
  ngOnDestroy() {
    this.languageChangeSubscription.unsubscribe();
  }
}
